import { Actor, HttpAgent, type Identity } from "@icp-sdk/core/agent";
import { Principal } from "@icp-sdk/core/principal";
import type * as LuckyGiftTypes from "@/lib/idl/lucky_gift_idl";
import type { _SERVICE } from "@/lib/idl/lucky_gift_idl";
import { idlFactory } from "@/lib/idl/lucky_gift_idl.js";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const clientLog = createLogAdapter("lib/clients/LuckyGiftClient.ts");

// 重新导出类型供外部使用
export type {
	ClaimCondition,
	ClaimFeeRewardsResponse,
	// 领取手续费相关
	ClaimFeeStats,
	ClaimRecord,
	ClaimRecordWithPrize,
	ClaimResponse,
	ClaimViralRewardsResponse,
	CreateGiftParams,
	CreateGiftResponse,
	CursorType,
	Direction,
	FeeTokenConfig,
	GiftInfo,
	GiftPacket,
	GiftStatus,
	GiftType,
	HoldTokenCondition,
	// 抽奖红包相关
	LotteryCondition,
	LotteryPrize,
	MyClaimRecordEnriched,
	// 我的邀请奖励红包相关
	MyInviteRewardGiftSummary,
	MyInviteRewardGiftsResponse,
	PaginatedResult_1,
	PasswordCondition,
	PreClaimGiftResponse,
	PublicGiftFilter,
	PublicGiftSummary,
	PublicGiftsResponse,
	RefundResponse,
	// 排序相关
	SortConfig,
	SortField,
	SortOrder,
	SystemControls,
	SystemStats,
	TokenConfig,
	TokenInfo,
	UserInviteStats,
	ViralGiftConfig,
	ViralParams,
	ViralStats,
} from "@/lib/idl/lucky_gift_idl";

/**
 * Lucky Gift Canister 客户端
 * 封装所有红包相关的操作
 *
 * Canister ID: oefvx-fqaaa-aaaac-a7wwa-cai
 */
export class LuckyGiftClient {
	private actor: _SERVICE;
	private canisterId = "oefvx-fqaaa-aaaac-a7wwa-cai";

	constructor(identity: Identity | null = null) {
		try {
			Principal.fromText(this.canisterId);

			const agentOptions = identity ? { identity } : {};

			const agent = new HttpAgent({
				host: process.env.NEXT_PUBLIC_IC_HOST || "https://ic0.app",
				verifyQuerySignatures: false,
				...agentOptions,
			});

			if (process.env.NODE_ENV === "development") {
				agent.fetchRootKey().catch((err) => {
					clientLog.warn("Failed to fetch root key:", err);
				});
			}

			this.actor = Actor.createActor<_SERVICE>(idlFactory, {
				agent,
				canisterId: this.canisterId,
			});
		} catch (error) {
			clientLog.error("Failed to initialize LuckyGiftClient:", error);
			throw new Error(`Invalid canister ID: ${this.canisterId}`);
		}
	}

	// ================= 红包创建与管理 =================

	/**
	 * 创建红包
	 * @returns 红包ID、所需充值金额、手续费信息
	 */
	async createGift(
		params: LuckyGiftTypes.CreateGiftParams,
	): Promise<LuckyGiftTypes.CreateGiftResponse> {
		try {
			const result = await this.actor.createGift(params);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error creating gift:", error);
			throw error;
		}
	}

	/**
	 * 确认充值，激活红包
	 * @param giftId 红包ID
	 * @param depositOpId 红包代币充值的 operationId
	 * @param feeOpId 可选，手续费充值的 operationId（如果分开转账）
	 */
	async confirmDeposit(
		giftId: string,
		depositOpId: bigint,
		feeOpId?: bigint,
	): Promise<void> {
		try {
			const result = await this.actor.confirmDeposit(
				giftId,
				depositOpId,
				feeOpId ? [feeOpId] : [],
			);
			if ("err" in result) throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error confirming deposit:", error);
			throw error;
		}
	}

	/**
	 * 取消红包（仅限 Pending 状态）
	 */
	async cancelGift(giftId: string): Promise<void> {
		try {
			const result = await this.actor.cancelGift(giftId);
			if ("err" in result) throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error canceling gift:", error);
			throw error;
		}
	}

	/**
	 * 退回红包（过期后退回剩余金额）
	 */
	async refundGift(giftId: string): Promise<LuckyGiftTypes.RefundResponse> {
		try {
			const result = await this.actor.refundGift(giftId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error refunding gift:", error);
			throw error;
		}
	}

	// ================= 领取红包 =================

	/**
	 * 领取前预检查（不消耗 Gas，不写入状态）
	 * @param giftId 红包ID
	 * @param recipient 可选，接收者
	 * @param inviteCode 可选，邀请码
	 * @param passwordProof 可选，口令证明
	 * @returns 预检查结果，包含是否需要支付领取手续费
	 */
	async preClaimGift(
		giftId: string,
		recipient?: Principal,
		inviteCode?: string,
		passwordProof?: Uint8Array,
	): Promise<LuckyGiftTypes.PreClaimGiftResponse> {
		try {
			const result = await this.actor.preClaimGift(
				giftId,
				recipient ? [recipient] : [],
				inviteCode ? [inviteCode] : [],
				passwordProof ? [passwordProof] : [],
			);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error pre-claiming gift:", error);
			throw error;
		}
	}

	/**
	 * 领取红包
	 * @param giftId 红包ID
	 * @param recipient 可选，接收者（代领模式，仅当条件为 Composite 且包含 None+Whitelist 且不包含 Login 时允许）
	 * @param inviteCode 可选，邀请码（裂变模式）
	 * @param passwordProof 可选，口令证明（口令红包使用，SHA256(password + salt)）
	 * @param feeOperationId 可选，领取手续费的转账 operationId（付费红包使用）
	 */
	async claimGift(
		giftId: string,
		recipient?: Principal,
		inviteCode?: string,
		passwordProof?: Uint8Array,
		feeOperationId?: bigint,
	): Promise<LuckyGiftTypes.ClaimResponse> {
		try {
			const result = await this.actor.claimGift(
				giftId,
				recipient ? [recipient] : [],
				inviteCode ? [inviteCode] : [],
				passwordProof ? [passwordProof] : [],
				feeOperationId !== undefined ? [feeOperationId] : [],
			);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error claiming gift:", error);
			throw error;
		}
	}

	// ================= 口令红包工具方法 =================

	/**
	 * 生成口令红包的 salt 和 passwordHash
	 * @param password 用户输入的口令明文
	 * @returns { salt, passwordHash } 用于创建口令红包
	 */
	static async generatePasswordCondition(password: string): Promise<{
		salt: Uint8Array;
		passwordHash: Uint8Array;
	}> {
		const salt = crypto.getRandomValues(new Uint8Array(32));
		const passwordBytes = new TextEncoder().encode(password);
		const combined = new Uint8Array([...passwordBytes, ...salt]);
		const hashBuffer = await crypto.subtle.digest("SHA-256", combined);
		const passwordHash = new Uint8Array(hashBuffer);
		return { salt, passwordHash };
	}

	/**
	 * 计算口令证明（用于领取口令红包）
	 * @param password 用户输入的口令
	 * @param salt 从红包条件中获取的 salt
	 * @returns passwordProof 用于 claimGift
	 */
	static async computePasswordProof(
		password: string,
		salt: Uint8Array | number[],
	): Promise<Uint8Array> {
		const passwordBytes = new TextEncoder().encode(password);
		const saltArray = salt instanceof Uint8Array ? salt : new Uint8Array(salt);
		const combined = new Uint8Array([...passwordBytes, ...saltArray]);
		const hashBuffer = await crypto.subtle.digest("SHA-256", combined);
		return new Uint8Array(hashBuffer);
	}

	// ================= 查询接口 =================

	/**
	 * 获取红包详情
	 * @param giftId 红包ID
	 * @param caller 可选，调用者（用于判断是否可领取）
	 */
	async getGiftInfo(
		giftId: string,
		caller?: Principal,
	): Promise<LuckyGiftTypes.GiftInfo | null> {
		try {
			const result = await this.actor.getGiftInfo(
				giftId,
				caller ? [caller] : [],
			);
			return result[0] ?? null;
		} catch (error) {
			clientLog.error("Error getting gift info:", error);
			throw error;
		}
	}

	/**
	 * 获取公开活跃红包列表（红包广场）
	 * @param filter 筛选条件
	 * @param sort 排序配置
	 * @param limit 每页数量
	 * @param offset 偏移量
	 */
	async getPublicActiveGifts(
		filter: LuckyGiftTypes.PublicGiftFilter | null,
		sort: LuckyGiftTypes.SortConfig | null,
		limit: bigint,
		offset: bigint,
	): Promise<LuckyGiftTypes.PublicGiftsResponse> {
		try {
			return await this.actor.getPublicActiveGifts(
				filter ? [filter] : [],
				sort ? [sort] : [],
				limit,
				offset,
			);
		} catch (error) {
			clientLog.error("Error getting public gifts:", error);
			throw error;
		}
	}

	/**
	 * 获取我创建的红包
	 */
	async getMyCreatedGifts(): Promise<LuckyGiftTypes.GiftPacket[]> {
		try {
			return await this.actor.getMyCreatedGifts();
		} catch (error) {
			clientLog.error("Error getting my created gifts:", error);
			throw error;
		}
	}

	/**
	 * 获取我的领取记录
	 */
	async getMyClaimRecords(): Promise<LuckyGiftTypes.MyClaimRecordEnriched[]> {
		try {
			return await this.actor.getMyClaimRecords();
		} catch (error) {
			clientLog.error("Error getting my claim records:", error);
			throw error;
		}
	}

	/**
	 * 查询所有领取记录（全局动态）
	 * @param cursor 可选，分页游标
	 * @param direction 分页方向，Forward 或 Backward
	 * @param limit 每页数量
	 * @returns 分页结果，包含领取记录列表
	 */
	async queryAllClaimRecords(
		cursor?: bigint,
		direction: LuckyGiftTypes.Direction = { Forward: null },
		limit: bigint = 20n,
	): Promise<LuckyGiftTypes.PaginatedResult_1> {
		try {
			return await this.actor.queryAllClaimRecords(
				cursor !== undefined ? [cursor] : [],
				direction,
				limit,
			);
		} catch (error) {
			clientLog.error("Error querying all claim records:", error);
			throw error;
		}
	}

	// ================= 配置查询 =================

	/**
	 * 获取支持的红包代币配置
	 */
	async getTokenConfigs(): Promise<
		Array<[string, LuckyGiftTypes.TokenConfig]>
	> {
		try {
			return await this.actor.getTokenConfigs();
		} catch (error) {
			clientLog.error("Error getting token configs:", error);
			throw error;
		}
	}

	/**
	 * 获取支持的手续费代币配置
	 */
	async getFeeTokenConfigs(): Promise<
		Array<[string, LuckyGiftTypes.FeeTokenConfig]>
	> {
		try {
			return await this.actor.getFeeTokenConfigs();
		} catch (error) {
			clientLog.error("Error getting fee token configs:", error);
			throw error;
		}
	}

	/**
	 * 获取系统控制状态
	 */
	async getSystemControls(): Promise<LuckyGiftTypes.SystemControls> {
		try {
			return await this.actor.getSystemControls();
		} catch (error) {
			clientLog.error("Error getting system controls:", error);
			throw error;
		}
	}

	/**
	 * 获取系统统计数据
	 */
	async getSystemStats(): Promise<LuckyGiftTypes.SystemStats> {
		try {
			return await this.actor.getSystemStats();
		} catch (error) {
			clientLog.error("Error getting system stats:", error);
			throw error;
		}
	}

	/**
	 * 获取 Canister ID
	 */
	async getCanisterId(): Promise<Principal> {
		try {
			return await this.actor.getCanisterId();
		} catch (error) {
			clientLog.error("Error getting canister id:", error);
			throw error;
		}
	}

	/**
	 * 获取 Canister 的本地 ID（不需要网络请求）
	 */
	getLocalCanisterId(): string {
		return this.canisterId;
	}

	// ================= 裂变功能 =================

	/**
	 * 生成邀请码
	 */
	async generateInviteCode(giftId: string): Promise<string> {
		try {
			const result = await this.actor.generateInviteCode(giftId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error generating invite code:", error);
			throw error;
		}
	}

	/**
	 * 获取裂变配置
	 */
	async getViralConfig(
		giftId: string,
	): Promise<LuckyGiftTypes.ViralGiftConfig | null> {
		try {
			const result = await this.actor.getViralConfig(giftId);
			return result[0] ?? null;
		} catch (error) {
			clientLog.error("Error getting viral config:", error);
			throw error;
		}
	}

	/**
	 * 获取裂变统计
	 */
	async getViralStats(giftId: string): Promise<LuckyGiftTypes.ViralStats> {
		try {
			return await this.actor.getViralStats(giftId);
		} catch (error) {
			clientLog.error("Error getting viral stats:", error);
			throw error;
		}
	}

	/**
	 * 获取我的邀请统计
	 */
	async getMyInviteStats(
		giftId: string,
	): Promise<LuckyGiftTypes.UserInviteStats> {
		try {
			return await this.actor.getMyInviteStats(giftId);
		} catch (error) {
			clientLog.error("Error getting my invite stats:", error);
			throw error;
		}
	}

	/**
	 * 领取裂变奖励（红包结束后一次性领取）
	 * @param giftId 红包ID
	 * @returns 奖励领取结果
	 */
	async claimViralRewards(
		giftId: string,
	): Promise<LuckyGiftTypes.ClaimViralRewardsResponse> {
		try {
			const result = await this.actor.claimViralRewards(giftId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error claiming viral rewards:", error);
			throw error;
		}
	}

	/**
	 * 获取我的邀请奖励红包列表
	 * 以登录用户为视角返回"参与邀请的红包列表"
	 * @param limit 每页数量（最大100）
	 * @param offset 偏移量
	 * @returns 邀请奖励红包列表
	 */
	async getMyInviteRewardGifts(
		limit: bigint,
		offset: bigint,
	): Promise<LuckyGiftTypes.MyInviteRewardGiftsResponse> {
		try {
			return await this.actor.getMyInviteRewardGifts(limit, offset);
		} catch (error) {
			clientLog.error("Error getting my invite reward gifts:", error);
			throw error;
		}
	}

	// ================= 领取手续费功能 =================

	/**
	 * 获取红包的领取手续费统计
	 * @param giftId 红包ID
	 * @returns 手续费统计信息
	 */
	async getClaimFeeStats(
		giftId: string,
	): Promise<LuckyGiftTypes.ClaimFeeStats> {
		try {
			const result = await this.actor.getClaimFeeStats(giftId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error getting claim fee stats:", error);
			throw error;
		}
	}

	/**
	 * 领取手续费收益（红包创建者调用）
	 * @param giftId 红包ID
	 * @returns 手续费领取结果
	 */
	async claimFeeRewards(
		giftId: string,
	): Promise<LuckyGiftTypes.ClaimFeeRewardsResponse> {
		try {
			const result = await this.actor.claimFeeRewards(giftId);
			if ("ok" in result) return result.ok;
			throw new Error(result.err);
		} catch (error) {
			clientLog.error("Error claiming fee rewards:", error);
			throw error;
		}
	}

	// ================= 工具方法 =================

	/**
	 * 格式化金额显示
	 * @param amount 链上金额（bigint）
	 * @param decimals 精度
	 */
	static formatAmount(amount: bigint, decimals: number): string {
		const divisor = BigInt(10 ** decimals);
		const intPart = amount / divisor;
		const fracPart = amount % divisor;

		if (fracPart === 0n) {
			return intPart.toString();
		}

		const fracStr = fracPart
			.toString()
			.padStart(decimals, "0")
			.replace(/0+$/, "");
		return `${intPart}.${fracStr}`;
	}

	/**
	 * 解析显示金额为链上金额
	 * @param displayAmount 显示金额
	 * @param decimals 精度
	 */
	static parseAmount(displayAmount: number | string, decimals: number): bigint {
		const amount =
			typeof displayAmount === "string"
				? parseFloat(displayAmount)
				: displayAmount;
		return BigInt(Math.floor(amount * 10 ** decimals));
	}

	/**
	 * 获取红包状态文本
	 */
	static getStatusText(
		status: LuckyGiftTypes.GiftStatus,
		language: "zh" | "en" = "zh",
	): string {
		const statusMap = {
			zh: {
				Pending: "待激活",
				Active: "进行中",
				Completed: "已领完",
				Expired: "已过期",
				Refunded: "已退回",
				Cancelled: "已取消",
			},
			en: {
				Pending: "Pending",
				Active: "Active",
				Completed: "Completed",
				Expired: "Expired",
				Refunded: "Refunded",
				Cancelled: "Cancelled",
			},
		};

		const key = Object.keys(status)[0] as keyof typeof statusMap.zh;
		return statusMap[language][key] || key;
	}

	/**
	 * 获取红包类型文本
	 */
	static getTypeText(
		giftType: LuckyGiftTypes.GiftType,
		language: "zh" | "en" = "zh",
	): string {
		const typeMap = {
			zh: {
				Random: "拼手气红包",
				Fixed: "普通红包",
			},
			en: {
				Random: "Lucky Draw",
				Fixed: "Fixed Amount",
			},
		};

		const key = Object.keys(giftType)[0] as keyof typeof typeMap.zh;
		return typeMap[language][key] || key;
	}

	/**
	 * 获取领取条件文本
	 */
	static getConditionText(
		condition: LuckyGiftTypes.ClaimCondition,
		language: "zh" | "en" = "zh",
	): string {
		const conditionMap = {
			zh: {
				None: "无条件",
				Login: "需登录",
				Whitelist: "白名单",
				HoldToken: "持仓验证",
				Password: "口令红包",
				Lottery: "抽奖红包",
				Composite: "组合条件",
			},
			en: {
				None: "No Condition",
				Login: "Login Required",
				Whitelist: "Whitelist",
				HoldToken: "Hold Token",
				Password: "Password",
				Lottery: "Lottery",
				Composite: "Composite",
			},
		};

		const key = Object.keys(condition)[0] as keyof typeof conditionMap.zh;
		return conditionMap[language][key] || key;
	}

	/**
	 * 检查红包是否可领取
	 */
	static isClaimable(
		gift: LuckyGiftTypes.GiftPacket | LuckyGiftTypes.PublicGiftSummary,
	): boolean {
		const now = BigInt(Date.now()) * 1_000_000n; // 转换为纳秒

		// 检查是否有 status 字段（GiftPacket 有，PublicGiftSummary 没有）
		if ("status" in gift) {
			if (!("Active" in gift.status)) return false;
		}

		// 检查是否过期
		if (now > gift.expireTime) return false;

		// 检查是否有剩余
		if ("remainingCount" in gift) {
			return gift.remainingCount > 0n;
		}

		if ("claimedCount" in gift && "totalCount" in gift) {
			return gift.claimedCount < gift.totalCount;
		}

		return true;
	}

	/**
	 * 计算红包剩余时间
	 */
	static getRemainingTime(
		expireTime: bigint,
		language: "zh" | "en" = "zh",
	): string {
		const now = BigInt(Date.now()) * 1_000_000n;
		const remaining = expireTime - now;

		if (remaining <= 0n) {
			return language === "zh" ? "已过期" : "Expired";
		}

		const hours = Number(remaining / 3_600_000_000_000n);
		const minutes = Number((remaining % 3_600_000_000_000n) / 60_000_000_000n);

		if (hours > 24) {
			const days = Math.floor(hours / 24);
			return language === "zh" ? `${days}天后过期` : `${days}d left`;
		}

		if (hours > 0) {
			return language === "zh"
				? `${hours}小时${minutes}分钟`
				: `${hours}h ${minutes}m`;
		}

		return language === "zh" ? `${minutes}分钟` : `${minutes}m`;
	}
}
